import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Upload, FileText, Trash2, Download, Search, FolderOpen,
  File, FileSpreadsheet, Image, Presentation, Archive,
  Loader2, MoreVertical, Tag, Eye, X, AlertCircle, HardDrive
} from 'lucide-react';
import { toast } from 'sonner';
import { documentLibraryAPI } from '../lib/api';

const CATEGORY_META = {
  price_list: { label: 'Price List', color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20' },
  offer_sheet: { label: 'Offer Sheet', color: 'text-amber-600 bg-amber-500/10 border-amber-500/20' },
  brochure: { label: 'Brochure', color: 'text-blue-600 bg-blue-500/10 border-blue-500/20' },
  certificate: { label: 'Certificate', color: 'text-purple-600 bg-purple-500/10 border-purple-500/20' },
  contract: { label: 'Contract', color: 'text-red-600 bg-red-500/10 border-red-500/20' },
  proposal: { label: 'Proposal', color: 'text-cyan-600 bg-cyan-500/10 border-cyan-500/20' },
  case_study: { label: 'Case Study', color: 'text-indigo-600 bg-indigo-500/10 border-indigo-500/20' },
  presentation: { label: 'Presentation', color: 'text-orange-600 bg-orange-500/10 border-orange-500/20' },
  compliance: { label: 'Compliance', color: 'text-rose-600 bg-rose-500/10 border-rose-500/20' },
  template: { label: 'Template', color: 'text-teal-600 bg-teal-500/10 border-teal-500/20' },
  other: { label: 'Other', color: 'text-gray-600 bg-gray-500/10 border-gray-500/20' },
};

const FILE_ICONS = {
  'application/pdf': FileText,
  'application/msword': FileText,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': FileText,
  'application/vnd.ms-excel': FileSpreadsheet,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': FileSpreadsheet,
  'application/vnd.ms-powerpoint': Presentation,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': Presentation,
  'image/jpeg': Image, 'image/png': Image, 'image/gif': Image, 'image/webp': Image,
  'application/zip': Archive, 'application/x-rar-compressed': Archive,
};

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_FILE_SIZE = 25 * 1024 * 1024;
const INLINE_LIMIT = 10 * 1024 * 1024;

// ── Upload Dialog ────────────────────────────────────────
function UploadDialog({ open, onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState('other');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  const handleFiles = (e) => {
    const selected = Array.from(e.target.files || []);
    const validated = selected.filter(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 25 MB limit`);
        return false;
      }
      return true;
    });
    setFiles(prev => [...prev, ...validated]);
  };

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const handleUpload = async () => {
    if (files.length === 0) { toast.error('Select at least one file'); return; }
    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', file.name);
        fd.append('category', category);
        fd.append('tags', tags);
        fd.append('description', description);
        await documentLibraryAPI.upload(fd);
        uploaded++;
        setProgress(Math.round((uploaded / files.length) * 100));
      } catch (e) {
        toast.error(`Failed to upload ${file.name}: ${e.response?.data?.detail || 'Error'}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} file(s) uploaded`);
      onUploaded();
      onClose();
      setFiles([]); setCategory('other'); setDescription(''); setTags('');
    }
    setUploading(false);
    setProgress(0);
  };

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5 text-primary" /> Upload Documents</DialogTitle>
          <DialogDescription>Add files to your document library for email attachments and workflows.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Drop zone */}
          <div
            className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/20 transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="upload-dropzone"
          >
            <Upload className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm font-medium">Click to browse or drag files</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, DOC, XLS, PPT, IMG, ZIP — Max 25 MB each</p>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={handleFiles} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.csv,.txt,.json,.zip,.rar" />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-1.5">
              {files.map((f, i) => {
                const Icon = FILE_ICONS[f.type] || File;
                return (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 bg-muted/20 rounded-lg text-sm">
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate flex-1">{f.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatSize(f.size)}</span>
                    {f.size > INLINE_LIMIT && (
                      <Badge variant="outline" className="text-xs shrink-0 border-amber-500/30 text-amber-600">Link</Badge>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeFile(i)}><X className="h-3.5 w-3.5" /></Button>
                  </div>
                );
              })}
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1 pt-1">
                <span>{files.length} file(s), {formatSize(totalSize)} total</span>
                {totalSize > INLINE_LIMIT && (
                  <span className="flex items-center gap-1 text-amber-600"><AlertCircle className="h-3 w-3" /> Files &gt;10 MB sent as download links</span>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-1" data-testid="upload-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tags (comma-separated)</Label>
              <Input value={tags} onChange={e => setTags(e.target.value)} placeholder="pricing, q1-2026" className="mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (optional)</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What is this document for?" rows={2} className="mt-1" />
          </div>

          {uploading && (
            <div className="space-y-1">
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-center text-muted-foreground">{progress}% uploaded</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleUpload} disabled={uploading || files.length === 0} data-testid="upload-submit-btn">
            {uploading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4 mr-1.5" /> Upload {files.length > 0 ? `(${files.length})` : ''}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ────────────────────────────────────────────
export default function DocumentLibrary() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);

  const loadDocs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (category !== 'all') params.category = category;
      if (search.trim()) params.search = search.trim();
      const res = await documentLibraryAPI.list(params);
      setDocuments(res.data?.documents || []);
    } catch {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [category, search]);

  useEffect(() => { loadDocs(); }, [loadDocs]);

  const handleDownload = async (doc) => {
    try {
      const res = await documentLibraryAPI.download(doc.id);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.original_filename || doc.name; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"?`)) return;
    try {
      await documentLibraryAPI.delete(doc.id);
      toast.success('Document deleted');
      loadDocs();
    } catch {
      toast.error('Delete failed');
    }
  };

  const totalSize = documents.reduce((s, d) => s + (d.size || 0), 0);

  return (
    <div className="space-y-6 p-1" data-testid="document-library-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <FolderOpen className="h-6 w-6 text-primary" />
            Document Library
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Upload and manage business documents for email attachments and workflows</p>
        </div>
        <Button onClick={() => setShowUpload(true)} data-testid="upload-btn" className="gap-1.5">
          <Upload className="h-4 w-4" /> Upload Documents
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-l-4 border-l-primary/60">
          <CardContent className="py-4 px-5">
            <p className="text-2xl font-bold">{documents.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Total Documents</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-emerald-500/60">
          <CardContent className="py-4 px-5">
            <p className="text-2xl font-bold">{formatSize(totalSize)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Storage Used</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500/60">
          <CardContent className="py-4 px-5">
            <p className="text-2xl font-bold">{documents.filter(d => d.attach_mode === 'inline').length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Inline Attachable</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500/60">
          <CardContent className="py-4 px-5">
            <p className="text-2xl font-bold">{documents.filter(d => d.attach_mode === 'link').length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Link-only (&gt;10MB)</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search documents..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="search-docs-input"
          />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Document list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading...
        </div>
      ) : documents.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-14 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <HardDrive className="h-7 w-7 text-muted-foreground/50" />
            </div>
            <p className="font-medium">No documents yet</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">Upload price lists, certificates, brochures, and more to use as email attachments</p>
            <Button className="mt-5 gap-1.5" onClick={() => setShowUpload(true)}>
              <Upload className="h-4 w-4" /> Upload Your First Document
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-2">
          {documents.map(doc => {
            const Icon = FILE_ICONS[doc.content_type] || File;
            const catMeta = CATEGORY_META[doc.category] || CATEGORY_META.other;
            return (
              <Card key={doc.id} className="group hover:border-primary/30 transition-all duration-200" data-testid={`doc-card-${doc.id}`}>
                <CardContent className="py-4 px-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-medium text-sm truncate">{doc.name}</h3>
                        <Badge variant="outline" className={`text-[10px] shrink-0 ${catMeta.color}`}>{catMeta.label}</Badge>
                        {doc.attach_mode === 'link' && (
                          <Badge variant="outline" className="text-[10px] shrink-0 border-amber-500/30 text-amber-600">Link only</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{formatSize(doc.size)}</span>
                        <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                        {doc.tags?.length > 0 && (
                          <span className="flex items-center gap-1"><Tag className="h-3 w-3" />{doc.tags.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 opacity-50 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="ghost" onClick={() => setSelectedDoc(doc)} title="Details" data-testid={`view-doc-${doc.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDownload(doc)} title="Download" data-testid={`download-doc-${doc.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 hover:bg-red-500/10" onClick={() => handleDelete(doc)} data-testid={`delete-doc-${doc.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <UploadDialog open={showUpload} onClose={() => setShowUpload(false)} onUploaded={loadDocs} />

      {/* Detail Dialog */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Document Details</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-3 text-sm">
              <div><Label className="text-xs text-muted-foreground">Name</Label><p className="font-medium">{selectedDoc.name}</p></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-muted-foreground">Size</Label><p>{formatSize(selectedDoc.size)}</p></div>
                <div><Label className="text-xs text-muted-foreground">Type</Label><p>{selectedDoc.content_type}</p></div>
                <div><Label className="text-xs text-muted-foreground">Category</Label><p>{CATEGORY_META[selectedDoc.category]?.label || selectedDoc.category}</p></div>
                <div><Label className="text-xs text-muted-foreground">Attach Mode</Label>
                  <p>{selectedDoc.attach_mode === 'inline' ? 'Direct Attachment' : 'Download Link (>10MB)'}</p>
                </div>
              </div>
              {selectedDoc.description && <div><Label className="text-xs text-muted-foreground">Description</Label><p>{selectedDoc.description}</p></div>}
              {selectedDoc.tags?.length > 0 && <div><Label className="text-xs text-muted-foreground">Tags</Label><p>{selectedDoc.tags.join(', ')}</p></div>}
              <div><Label className="text-xs text-muted-foreground">Uploaded</Label><p>{new Date(selectedDoc.created_at).toLocaleString()}</p></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectedDoc(null)}>Close</Button>
            <Button onClick={() => { handleDownload(selectedDoc); setSelectedDoc(null); }}>
              <Download className="h-4 w-4 mr-1.5" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
